/**
 * Business logic for activation flow step progression
 * Extracted from UnifiedActivationFlow to improve testability and readability
 */

import type { SubstepDefinition } from './stepDefinitions';
import { StepMode } from './stepDefinitions';
import type { BackendActivationStepIdType } from '../ActivationFlow/constants/stepIds';

/**
 * Result of step progression logic
 */
export interface StepProgressionAction {
  type:
    | 'MOVE_LOCAL'
    | 'UPDATE_BACKEND_THEN_MOVE'
    | 'COMPLETE'
    | 'UPDATE_BACKEND_THEN_COMPLETE';
  nextIndex?: number;
  backendStepToUpdate?: BackendActivationStepIdType;
}

/**
 * Context needed for step progression decisions
 */
export interface StepProgressionContext {
  currentStepIndex: number;
  visibleSubsteps: SubstepDefinition[];
  isFeatureEnabled: boolean; // USE_POST_ACCOUNT_FLOW
  furthestBackendStep?: BackendActivationStepIdType | null; // Furthest step user has reached on backend
}

/**
 * Checks if we're at the last step
 */
function isLastStep(currentIndex: number, totalSteps: number): boolean {
  return currentIndex + 1 >= totalSteps;
}

/**
 * Finds the most recent substep with a backendStep before the given index
 * Used for UI-only steps that need to update backend to last "real" step
 */
function findPreviousBackendStep(
  substeps: SubstepDefinition[],
  fromIndex: number,
): { step: BackendActivationStepIdType; index: number } | null {
  for (let i = fromIndex - 1; i >= 0; i--) {
    if (substeps[i]?.backendStep) {
      return {
        step: substeps[i].backendStep!,
        index: i,
      };
    }
  }
  return null;
}

/**
 * Checks if a step has already been completed on the backend
 * Returns true if the stepToCheck is at or before the furthestStep
 */
function isStepAlreadyCompleted(
  substeps: SubstepDefinition[],
  stepToCheck: BackendActivationStepIdType,
  furthestStep: BackendActivationStepIdType | null | undefined,
): boolean {
  if (!furthestStep) return false;

  // Find indices of both steps in the substeps array
  const stepToCheckIndex = substeps.findIndex(
    (s) => s.backendStep === stepToCheck,
  );
  const furthestStepIndex = substeps.findIndex(
    (s) => s.backendStep === furthestStep,
  );

  // If we found both steps, compare their indices
  if (stepToCheckIndex !== -1 && furthestStepIndex !== -1) {
    return stepToCheckIndex <= furthestStepIndex;
  }

  return false;
}

/**
 * Determines what action to take when progressing from current step
 *
 * This encapsulates all the complex business logic for step progression:
 * - Pre-account steps: just move locally
 * - Post-account steps WITH backendStep: update backend tracking (only if not already completed)
 * - Post-account steps WITHOUT backendStep: update to last backend step
 * - Already completed steps: skip backend call and just move locally
 */
export function determineStepProgression(
  context: StepProgressionContext,
): StepProgressionAction {
  const {
    currentStepIndex,
    visibleSubsteps,
    isFeatureEnabled,
    furthestBackendStep,
  } = context;

  const currentSubstep = visibleSubsteps[currentStepIndex];
  const nextIndex = currentStepIndex + 1;
  const isAtLastStep = isLastStep(currentStepIndex, visibleSubsteps.length);

  // ===================================
  // Case 1: Pre-Account Steps
  // ===================================
  // These are signup steps before account creation - no backend tracking needed
  if (currentSubstep.mode === StepMode.PreAccount) {
    return isAtLastStep
      ? { type: 'COMPLETE' }
      : { type: 'MOVE_LOCAL', nextIndex };
  }

  // ===================================
  // Case 2: Post-Account Steps WITH Backend Step
  // ===================================
  // These steps are tracked by backend (e.g., issue_card, top_up)
  if (
    currentSubstep.mode === StepMode.PostAccount &&
    currentSubstep.backendStep
  ) {
    // Backend disabled: skip all post-account steps
    if (!isFeatureEnabled) {
      return { type: 'COMPLETE' };
    }

    // Last step: check if already completed on backend
    if (isAtLastStep) {
      const alreadyCompleted = isStepAlreadyCompleted(
        visibleSubsteps,
        currentSubstep.backendStep,
        furthestBackendStep,
      );

      if (alreadyCompleted) {
        // Step already completed - just complete the flow
        console.log(
          `Step ${currentSubstep.backendStep} already completed on backend. Skipping update.`,
        );
        return { type: 'COMPLETE' };
      }

      // Not yet completed - update backend then complete
      return {
        type: 'UPDATE_BACKEND_THEN_COMPLETE',
        backendStepToUpdate: currentSubstep.backendStep,
      };
    }

    // Not last step: check if next step has backend tracking
    const nextSubstep = visibleSubsteps[nextIndex];

    if (nextSubstep.backendStep) {
      // Check if next step is already completed on backend
      const alreadyCompleted = isStepAlreadyCompleted(
        visibleSubsteps,
        nextSubstep.backendStep,
        furthestBackendStep,
      );

      if (alreadyCompleted) {
        // Next step already completed - just move locally without backend call
        console.log(
          `Next step ${nextSubstep.backendStep} already completed on backend. Skipping update.`,
        );
        return {
          type: 'MOVE_LOCAL',
          nextIndex,
        };
      }

      // Next step not yet completed - update backend to next step
      return {
        type: 'UPDATE_BACKEND_THEN_MOVE',
        nextIndex,
        backendStepToUpdate: nextSubstep.backendStep,
      };
    } else {
      // Next step is UI-only: just move locally (no backend update needed yet)
      return {
        type: 'MOVE_LOCAL',
        nextIndex,
      };
    }
  }

  // ===================================
  // Case 3: Post-Account Steps WITHOUT Backend Step (UI-only)
  // ===================================
  // These are UI-only steps (e.g., card_design, your_card_ready) that don't have their own backend tracking
  // Strategy: Check if NEXT step needs backend update
  if (
    currentSubstep.mode === StepMode.PostAccount &&
    !currentSubstep.backendStep
  ) {
    // Backend disabled: just move locally
    if (!isFeatureEnabled) {
      return isAtLastStep
        ? { type: 'COMPLETE' }
        : { type: 'MOVE_LOCAL', nextIndex };
    }

    // Last step: update to previous backend step
    if (isAtLastStep) {
      const previousBackendStep = findPreviousBackendStep(
        visibleSubsteps,
        currentStepIndex,
      );

      if (previousBackendStep) {
        const alreadyCompleted = isStepAlreadyCompleted(
          visibleSubsteps,
          previousBackendStep.step,
          furthestBackendStep,
        );

        if (alreadyCompleted) {
          console.log(
            `UI-only last step: Previous backend step ${previousBackendStep.step} already completed. Skipping update.`,
          );
          return { type: 'COMPLETE' };
        }

        return {
          type: 'UPDATE_BACKEND_THEN_COMPLETE',
          backendStepToUpdate: previousBackendStep.step,
        };
      }

      return { type: 'COMPLETE' };
    }

    // Not last step: check if NEXT step has backend tracking
    const nextSubstep = visibleSubsteps[nextIndex];

    if (nextSubstep.backendStep) {
      // Next step has backend tracking - check if already completed
      const alreadyCompleted = isStepAlreadyCompleted(
        visibleSubsteps,
        nextSubstep.backendStep,
        furthestBackendStep,
      );

      if (alreadyCompleted) {
        // Next step already completed - just move locally
        console.log(
          `UI-only step: Next step ${nextSubstep.backendStep} already completed. Skipping update.`,
        );
        return {
          type: 'MOVE_LOCAL',
          nextIndex,
        };
      }

      // Next step not completed - update backend to next step
      console.log(
        `UI-only step completed. Updating backend to next backend step: ${nextSubstep.backendStep}`,
      );
      return {
        type: 'UPDATE_BACKEND_THEN_MOVE',
        nextIndex,
        backendStepToUpdate: nextSubstep.backendStep,
      };
    } else {
      // Next step is also UI-only: just move locally
      return {
        type: 'MOVE_LOCAL',
        nextIndex,
      };
    }
  }

  // Fallback (should never reach here)
  console.error('Unexpected step configuration', currentSubstep);
  return { type: 'COMPLETE' };
}
